/* eslint-disable react-hooks/exhaustive-deps */
import React, { Suspense, useCallback, useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import * as Sentry from '@sentry/react';
import { everspanAndSCORStatesMapToPolicyType } from '@ourbranch/lookups';
import { policyTypes } from '@ourbranch/policy-types';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import isEqual from 'lodash-es/isEqual';

import { useStore } from 'core/store';
import { Loading } from 'core';
import { removeQueryParamsFromUrl } from 'core/helpers/url-utils';
import { useQueryParams } from 'core/hooks/useQueryParams';
import { AuthContext } from 'core/components/auth';
import ErrorDialog from 'core/components/error-dialog/error-dialog';
import LeavePageDialog from 'core/components/leave-page-dialog';
import Section from 'core/components/section';
import { useToast } from 'core/components/toast/context';
import {
  getPolicyStatus,
  PolicyStatus,
  getPolicyChange,
  isImportedAndNotEndorseable
} from 'core/helpers/policy-status';
import { PolicyTabs, PolicyModalTypes } from 'core/store/account-store/policies-store/policy-store/policy-store';
import { track } from 'core/helpers/analytics';
import useSession from 'core/hooks/use-session';
import { getErrorData } from 'common/constants/get-error-data';
import { haveAnyOfThisCarsSymbolsChanged } from 'common/helpers/car-symbols-helpers';
import { DisableProvider } from 'common/disabled-context';
import { prepareFarmPartyForSave } from 'common/helpers/farm-interested-party-helpers';
import PolicyDetails from './details';
import Incidents from './incidents';
import { PaymentTab } from './payment-tab';
import PolicySettings from './policy-settings';
import Preview from './preview';
import Documents from '../documents/documents';
import { PolicyNotifications } from './policy-notifications';
import { PolicyRestrictionsBanner } from './policy-restrictions-banner';
import { PolicyCommentsBanner } from './policy-comments-banner';
import RenewalNotice from './renewal-notice';
import { WarningModal } from './warning-modal';
import CancelledPolicyFooter from './footer/cancelled-policy-footer';
import ImportedPolicyFooter from './footer/imported-policy-footer';
import { getChangesForApproval } from './preview/helpers';
import {
  formatRequestedChangesText,
  parseRequestedChanges
} from './preview/policy-changes-list/parse-requested-changes';
import {
  buildRestrictionUwReviewAnswers,
  captureEditedRestriction,
  resolveEditedRestriction
} from './restriction-endorsement';
import {
  getFarmPolicyRestriction,
  wasRenewedFromImportedLineage
} from 'common/components/farm/uw-questions-form/helpers';
import { SubmittedForApprovalModal } from './submitted-for-approval-modal';
import { PolicyPreviewExpiredModal } from './policy-preview-expired-modal';
const FarmUWQuestionsModal = React.lazy(() =>
  import('./farm-uw-questions-modal').then((m) => ({ default: m.FarmUWQuestionsModal }))
);

import useStyles from './policy.styles';

const formatAdditionalParties = ({ additionalParties }) => {
  if (!Array.isArray(additionalParties)) {
    return additionalParties;
  }
  return additionalParties.map((party) => {
    if (party.address && !party.address.address) {
      return {
        ...party,
        address: {
          address: '',
          address2: party.address?.address2 || '',
          city: party.address?.city || '',
          state: party.address?.state || '',
          zip: party.address?.zip || ''
        }
      };
    }
    return party;
  });
};

const checkCanReinstatePolicy = ({
  cancelReason,
  policyStatus,
  session,
  canUserRescindOrReinstateGSNICPolicy,
  isCancelledGSNICPolicy
}) => {
  if (policyStatus !== PolicyStatus.Cancelled) {
    return false;
  }

  // gsnic logic
  if (isCancelledGSNICPolicy) {
    if (cancelReason === 'CNPY') {
      return session.canReinstateCNPY && canUserRescindOrReinstateGSNICPolicy;
    }
    return session.canRescindOrReinstateGSNICPolicy;
  }

  // bix logic
  if (cancelReason === 'CNPY') {
    return session.canReinstateCNPY;
  }

  return session.canReinstate;
};

const checkIfUserCanReinstateRescindGSNICPolicy = ({ isBix, nonRenewReason, session }) => {
  return !isBix
    ? nonRenewReason === 'NRSC'
      ? session?.canReinstate || session?.canRescindCancellation
      : session?.canRescindOrReinstateGSNICPolicy
    : false;
};

const checkCanRescindPolicy = ({
  isCancelledGSNICPolicy,
  canUserRescindOrReinstateGSNICPolicy,
  session,
  policyStatus
}) => {
  return isCancelledGSNICPolicy
    ? canUserRescindOrReinstateGSNICPolicy && policyStatus === PolicyStatus.InCancellation
    : session.canRescindCancellation && policyStatus === PolicyStatus.InCancellation;
};

const friendlyErrorMessage = (message) => {
  if (message?.toLowerCase()?.includes('only verified bank accounts can be used')) {
    return `Unable to make the ACH account the primary method of payment yet, because this ACH account has not yet been verified. To verify it, go to the member's page and confirm the microdeposits with the member.`;
  }
  return message;
};

const Policy = ({ history }) => {
  const classes = useStyles();
  const toast = useToast();
  const session = useContext(AuthContext);

  const {
    account: {
      fetchFullAccountAndPolicy,
      updateAccount,
      id: accountId,
      mailingAddress,
      fname,
      lname,
      email,
      phoneNumber,
      sendEventAsMember,
      policies: {
        notifications,
        list: policies,
        policy: {
          policy,
          preview: policyPreview,
          isPreviewExpired,
          loading,
          setChanged,
          changed,
          changePolicy,
          clearPolicyPreview,
          changeSegment,
          addSegment,
          previewPolicyChangeScrubIncidents,
          previewPolicyChangeAddIncidents,
          getRenewalPolicyId,
          confirm,
          submitChangesForApproval,
          submitFarmPolicyForUnderwritingApproval,
          currentTab,
          setCurrentTab,
          cancelled,
          nonRenewReason,
          cancelReason,
          revertPreview,
          revertLoading,
          activePolicyModal,
          setActivePolicyModal,
          closePolicyModal
        }
      }
    }
  } = useStore();

  const [promptTab, setPromptTab] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState({});
  const [preview, setPreview] = useState(policyPreview);
  // #32405: the Details "Policy restrictions" value is a form-only field (stripped from the segment
  // payload). Capture the UW-edited value once a preview is generated, tagged with that preview's id
  // ({ previewId, restrictions }), so handleConfirmChanges forwards it as uwReviewAnswers ONLY when
  // that same preview is confirmed. Keying by previewId is what survives the preview→confirm
  // round-trip: previously a bare ref was wiped by the policyPreview effect the moment the preview
  // store updated, so the edit never reached policy-mod (uwReviewAnswers arrived null — reopened bug).
  const editedRestrictionRef = useRef(undefined);
  const [action, setAction] = useState('');
  const [reasons, setReasons] = useState({});
  const [renewalPolicyId, setRenewalPolicyId] = useState(null);
  const [remountKey, setRemountKey] = useState(0);
  const [disableLeavePageModal, setDisableLeavePageModal] = useState(false);
  // Recoverable "Create New Change" date collision (#31068) — shown via the neutral WarningModal
  // instead of the red error dialog so closing returns to the form with the draft intact.
  const [showChangeDateConflict, setShowChangeDateConflict] = useState(false);

  const { isAgency, user, canApproveUWReview } = useSession();
  const { tab: tabFromQueryParams } = useQueryParams();
  const previewIdFromQueryParams = policyPreview?.id;

  // Set initial tab from query param if provided
  useEffect(() => {
    if (tabFromQueryParams && PolicyTabs[tabFromQueryParams.toUpperCase()]) {
      setCurrentTab(PolicyTabs[tabFromQueryParams.toUpperCase()]);
    }
  }, []);

  const changesForApprovalConfig = getChangesForApproval({ policy, preview, session });

  useEffect(() => {
    if (revertLoading) {
      setLoadingPreview(true);
      setActivePolicyModal(PolicyModalTypes.PREVIEW);
    } else if (revertPreview) {
      setLoadingPreview(false);
      setPreview(revertPreview);
    }
  }, [revertPreview, revertLoading, setLoadingPreview, setPreview, setActivePolicyModal]);

  useEffect(() => {
    if (isPreviewExpired) {
      setActivePolicyModal(PolicyModalTypes.PREVIEW_LINK_EXPIRED);
      return;
    }

    if (policyPreview) {
      setPreview(policyPreview);
      setCurrentTab(PolicyTabs.DETAILS);
      setActivePolicyModal(PolicyModalTypes.PREVIEW);
      // #32405: a preview loaded from a link carries no restriction edit. We no longer clear the ref
      // here — the capture is tagged with its own preview id, so resolveEditedRestriction only
      // applies it to the matching confirm; a link-loaded preview's id won't match. (Clearing here
      // was also wiping the in-page edit that had just been captured — the reopened bug.)
    }
  }, [policyPreview, isPreviewExpired]);

  useEffect(() => {
    if (currentTab !== PolicyTabs.DETAILS && preview) {
      removeQueryParamsFromUrl(history);
      setPreview(null);
    }
  }, [currentTab]);

  const isLoading = loading || !policy;
  const policyStatus = isLoading ? null : getPolicyStatus(policy);

  const cancelledOrInCancellation =
    policyStatus === PolicyStatus.InCancellation || policyStatus === PolicyStatus.Cancelled;

  // Disable navigation prompt for cancelled policies showing reinstate/rescind footers
  useEffect(() => {
    if (cancelledOrInCancellation) {
      setDisableLeavePageModal(true);
    } else {
      setDisableLeavePageModal(false);
    }
  }, [cancelledOrInCancellation]);

  const isCancelledGSNICPolicy =
    (cancelled || cancelledOrInCancellation) &&
    !policy.isBix &&
    everspanAndSCORStatesMapToPolicyType[policy.state]?.includes(policy.policyType);

  // there is some sort of issue using session or destructuring session before loading is fully done, so have to do this ternary
  const canUserRescindOrReinstateGSNICPolicy = isLoading
    ? false
    : checkIfUserCanReinstateRescindGSNICPolicy({
        isBix: policy?.isBix,
        nonRenewReason,
        session
      });

  // there is some sort of issue using session or destructuring session before loading is fully done, so have to do this ternary
  const allowReinstatement = isLoading
    ? false
    : checkCanReinstatePolicy({
        cancelReason,
        policyStatus,
        session,
        canUserRescindOrReinstateGSNICPolicy,
        isCancelledGSNICPolicy
      });

  // there is some sort of issue using session or destructuring session before loading is fully done, so have to do this ternary
  const allowRescind = isLoading
    ? false
    : checkCanRescindPolicy({
        isCancelledGSNICPolicy,
        canUserRescindOrReinstateGSNICPolicy,
        session,
        policyStatus
      });

  const setLoadingState = () => {
    setLoadingPreview(true);
    setActivePolicyModal(PolicyModalTypes.PREVIEW);
    setError({});
    setPreview(null);
  };

  const closeModal = () => {
    closePolicyModal();

    // Don't want to clear for farm policies so agents don't loose previews
    if (previewIdFromQueryParams && !isFarmBasedPolicyType(policy.policyType)) {
      removeQueryParamsFromUrl(history);
      clearPolicyPreview();
    }
  };

  const handleError = ({ error }, values) => {
    Sentry.captureException(
      JSON.stringify({
        res: error,
        ...values
      })
    );
    const { message, ...errorRest } = error;
    const defaultMessages = message
      ? [message]
      : [
          'Sorry about that. It looks like our systems are not able process a preview for the changes.',
          'Please try again'
        ];

    const defaultError = {
      messages: defaultMessages,
      title: 'Settings preview error',
      label: 'Try Again',
      tryAgain: error?.tryAgain || handleChangePolicy.bind(this, values),
      ...errorRest
    };

    let errorToDisplay;

    if (error?.networkError) {
      errorToDisplay = {
        messages: [
          'Our servers are not responding.',
          'You might have lost your internet connection, please check it.',
          'If not, just refresh this page.'
        ],
        label: 'Refresh',
        title: 'Whoops!',
        tryAgain: window.location.reload.bind(window.location)
      };
    }

    if (error?.message?.includes('Hardship moratorium zip code')) {
      errorToDisplay = {
        messages: [error.message],
        title: 'Hardship moratorium detected',
        label: 'Close',
        onClick: () => {
          setError({});
          closeModal();
        }
      };
    }

    if (error?.message?.includes('Mid-term Date Past Expiration Date')) {
      errorToDisplay = {
        messages: ['The transaction date is past the expiration date. Not enough advance notice days.'],
        title: 'Mid-term Date Past Expiration Date',
        label: 'Close',
        onClick: () => {
          setError({});
          closeModal();
        }
      };
    }

    if (!errorToDisplay) {
      errorToDisplay = defaultError;
    }
    setError(errorToDisplay);
    closePolicyModal();
    setAction(null);
  };

  const handleChangePolicy = useCallback(
    async (values) => {
      const currentPolicy = getPolicyStatus(policy);
      const newPolicy = getPolicyStatus(values);
      const action = getPolicyChange(currentPolicy, newPolicy);
      setLoadingState();

      // clear out cancel reason on reinstates and rescinds
      if ((allowRescind || allowReinstatement) && (action === 'reinstate' || action === 'rescind')) {
        values.cancelReason = 'NA';
        if (allowReinstatement && action === 'reinstate') {
          values.nonRenewReason = 'NA';
        }
      }
      const res = await changePolicy(
        {
          ...values,
          additionalParties: formatAdditionalParties(values),
          farmInterestedParties: values.farmInterestedParties
            ? values.farmInterestedParties.map(prepareFarmPartyForSave)
            : undefined
        },
        // `action` selects the dedicated cancel/reinstate preview mutation (issue #31365)
        action
      );
      if (res.ok) {
        const { preview, reasons } = res;

        preview.userSelectedEndDate = values?.transactionDate || values.endDate;
        setReasons(reasons);
        setPreview(preview);
        setAction(action);
      } else {
        handleError(res, { values });
        closeModal();
        setAction(null);
      }
      setLoadingPreview(false);
    },

    [policy, changePolicy, allowReinstatement, allowRescind]
  );

  const handleSegmentChange = useCallback(
    async (segment, changeType, initialValues) => {
      setLoadingState();
      setAction(null);
      const policyId = policy.id;
      const hasSymbols = (car) => haveAnyOfThisCarsSymbolsChanged(car, initialValues.cars);
      if (segment.cars?.some((car) => hasSymbols(car))) {
        segment.cars.forEach((car) => {
          if (hasSymbols(car)) {
            car.manuallyAddedSymbols = true;
            car.symbolMake = car.symbolMake?.toUpperCase();
            car.symbolModel = car.symbolModel?.toUpperCase();
            car.symbolStyle = car.symbolStyle?.toUpperCase();
            car.symbolAux = car.symbolAux?.toUpperCase();
            car.symbolPGS = car.symbolPGS?.toUpperCase() || ' ';
          }
        });
      }

      if (segment.umbrellaCoverage) {
        /**
         * This is a temporary fix until the use case is properly handled
         * @TODO: Support free input addresses for rental properties
         */
        const { rentalPropertyAddresses, otherPropertyAddresses } = segment.umbrellaCoverage || {};

        segment.umbrellaCoverage.rentalPropertyAddresses =
          rentalPropertyAddresses?.filter((address) => typeof address === 'object') || [];
        segment.umbrellaCoverage.otherPropertyAddresses =
          otherPropertyAddresses?.filter((address) => typeof address === 'object') || [];
      }

      const revisedSegment = { ...segment };
      if (policy.policyType !== 'A') {
        delete revisedSegment.drivers;
        delete revisedSegment.autoCoverage;
      }

      // Extract any pending additional parties (lienholders added via Add Car form)
      // before passing the segment to the mutation, since PolicyDetailsInput doesn't include additionalParties.
      // These are sent to the backend via the addSegment mutation and applied to the policy on confirm.
      const newAdditionalParties = revisedSegment.additionalParties;
      delete revisedSegment.additionalParties;
      let pendingAdditionalParties;
      if (changeType !== 'edit_change' && Array.isArray(newAdditionalParties) && newAdditionalParties.length > 0) {
        const initialPartyIds = new Set(
          (initialValues.additionalParties || [])
            .filter((p) => p.id) // Only include parties with valid IDs
            .map((p) => p.id)
        );
        const newParties = (newAdditionalParties || []).filter((p) => !initialPartyIds.has(p.id));
        if (newParties.length > 0) {
          pendingAdditionalParties = newParties;
        }
      }

      const res =
        changeType === 'edit_change'
          ? await changeSegment(policyId, revisedSegment)
          : await addSegment(policyId, revisedSegment, { pendingAdditionalParties });
      if (res.ok) {
        const { preview } = res;
        // #32405: capture the UW-edited restriction (form-only; sanitize.js strips it from the
        // segment payload) tagged with this preview's id, so handleConfirmChanges forwards it only
        // when this exact preview is confirmed. Done AFTER the preview resolves — the policyPreview
        // effect fires on the store update, and capturing beforehand would be immediately lost.
        editedRestrictionRef.current = captureEditedRestriction(preview.id, segment.restrictions);
        setPreview(preview);
        if (preview.warning) {
          setActivePolicyModal(PolicyModalTypes.WARNING);
        }

        if (policy.policyType === policyTypes.Auto) {
          // Check if any drivers' PII (dateOfBirth or driversLicenseNumber) has changed & fire segment events
          const driversWithPIIChanges = [];

          if (!segment.drivers || !initialValues.drivers) {
            // Log if drivers data is missing to help debug issues
            Sentry.captureMessage('Driver PII tracking skipped - missing driver data', {
              level: 'info',
              extra: {
                policyId: policy.id,
                hasSegmentDrivers: !!segment.drivers,
                hasInitialDrivers: !!initialValues.drivers,
                segmentDriversCount: segment.drivers?.length || 0,
                initialDriversCount: initialValues.drivers?.length || 0
              }
            });
          }

          if (segment.drivers && initialValues.drivers) {
            const normalizeString = (str) => (str || '').trim().toLowerCase();
            segment.drivers.forEach((segmentDriver) => {
              const initialDriver = initialValues.drivers.find(
                (initial) =>
                  normalizeString(initial.firstName) === normalizeString(segmentDriver.firstName) &&
                  normalizeString(initial.middleName) === normalizeString(segmentDriver.middleName) &&
                  normalizeString(initial.lastName) === normalizeString(segmentDriver.lastName)
              );

              // Log if driver matching fails
              if (!initialDriver) {
                Sentry.captureMessage('Driver PII tracking - driver match failed', {
                  level: 'info',
                  extra: {
                    policyId: policy.id,
                    segmentDriverName:
                      `${segmentDriver.firstName} ${segmentDriver.middleName || ''} ${segmentDriver.lastName}`.trim(),
                    initialDriverNames: initialValues.drivers.map((d) =>
                      `${d.firstName} ${d.middleName || ''} ${d.lastName}`.trim()
                    )
                  }
                });
              }

              if (
                initialDriver &&
                (normalizeString(segmentDriver.dateOfBirth) !== normalizeString(initialDriver.dateOfBirth) ||
                  normalizeString(segmentDriver.driversLicenseNumber) !==
                    normalizeString(initialDriver.driversLicenseNumber))
              ) {
                driversWithPIIChanges.push({
                  driver: segmentDriver,
                  changes: {
                    dateOfBirth:
                      normalizeString(segmentDriver.dateOfBirth) !== normalizeString(initialDriver.dateOfBirth),
                    driversLicenseNumber:
                      normalizeString(segmentDriver.driversLicenseNumber) !==
                      normalizeString(initialDriver.driversLicenseNumber)
                  }
                });
              }
            });
          }

          if (driversWithPIIChanges.length) {
            // Log that we're about to send events
            Sentry.captureMessage('Driver PII tracking - sending events', {
              level: 'info',
              extra: {
                policyId: policy.id,
                webUserId: policy.webUserId,
                driversCount: driversWithPIIChanges.length,
                drivers: driversWithPIIChanges.map(({ driver, changes }) => ({
                  name: `${driver.firstName} ${driver.lastName}`,
                  updatedDOB: changes.dateOfBirth,
                  updatedDL: changes.driversLicenseNumber
                }))
              }
            });

            // Check if webUserId is missing
            if (!policy.webUserId) {
              Sentry.captureMessage('Driver PII tracking failed - missing webUserId', {
                level: 'warning',
                extra: {
                  policyId: policy.id,
                  driversCount: driversWithPIIChanges.length
                }
              });
            } else {
              for (const { driver, changes } of driversWithPIIChanges) {
                try {
                  const result = await sendEventAsMember({
                    eventName: 'Driver PII Submitted',
                    userId: policy.webUserId,
                    properties: JSON.stringify({
                      driverName: `${driver.firstName} ${driver.lastName}`,
                      policyId: policy.id,
                      updatedDateOfBirth: changes.dateOfBirth,
                      updatedDriversLicenseNumber: changes.driversLicenseNumber
                    })
                  });

                  // Log if the event sending failed
                  if (result?.error) {
                    Sentry.captureException(new Error('Driver PII event sending failed'), {
                      extra: {
                        policyId: policy.id,
                        webUserId: policy.webUserId,
                        driverName: `${driver.firstName} ${driver.lastName}`,
                        error: result.error
                      }
                    });
                  }
                } catch (error) {
                  Sentry.captureException(error, {
                    extra: {
                      context: 'Driver PII event sending',
                      policyId: policy.id,
                      webUserId: policy.webUserId,
                      driverName: `${driver.firstName} ${driver.lastName}`
                    }
                  });
                }
              }
            }
          }
        }
      } else if (res.error?.message?.includes('Segment already exists with this start date')) {
        // Recoverable date collision (#31068): the chosen "Create New Change" date already has a
        // change on it. Surface the neutral, single-Close WarningModal rather than the red error
        // dialog — closing leaves the draft intact so the agent can pick a different date or
        // switch to "Edit Selected Change".
        setShowChangeDateConflict(true);
        setPreview(null);
        closePolicyModal();
      } else {
        let message = res.error?.message;
        if (!isNaN(+res.error?.message)) {
          // Only use getErrorData if we're confident this is an actual error code
          const errorCode = parseInt(res.error.message, 10);
          const errorData = errorCode ? getErrorData({ error: { code: errorCode }, state: policy.state }) : null;
          message = (errorData?.policyErrorMessage || errorData?.why) ?? res.error.message;
        }
        handleError(
          {
            error: {
              message,
              tryAgain: handleSegmentChange.bind(this, segment, changeType, initialValues)
            }
          },
          { revisedSegment }
        );
        setPreview(null);
        closePolicyModal();
      }
      setLoadingPreview(false);
    },
    [policy, addSegment, changeSegment]
  );

  const handleChangeTab = useCallback(
    (e, tab) => {
      if ((changed && !disableLeavePageModal) || previewIdFromQueryParams) {
        e.preventDefault();
        setPromptTab(tab);
      } else {
        setCurrentTab(tab);
      }
      // used for appcues to work when needed, do not need for the settings tab
      if (tab !== PolicyTabs.SETTINGS) {
        track(`Staff Appcues: ${tab} Tab Clicked`, {
          isAgency,
          affinityCode: user.affinityCode
        });
      }
    },
    [changed, setCurrentTab, previewIdFromQueryParams, disableLeavePageModal]
  );

  const handlePreviewPolicyChangeScrubIncidents = useCallback(
    async (policyValue, incidentDates, claimsToScrub) => {
      setLoadingState();
      const res = await previewPolicyChangeScrubIncidents(policyValue, incidentDates, claimsToScrub);
      if (res.ok) {
        const { preview } = res;
        setPreview(preview);
      } else {
        handleError(
          {
            ...res,
            tryAgain: handlePreviewPolicyChangeScrubIncidents.bind(this, policyValue, incidentDates, claimsToScrub)
          },
          { policyValue }
        );
      }
      setLoadingPreview(false);
    },
    [previewPolicyChangeScrubIncidents]
  );

  const handlePreviewPolicyChangeAddIncidents = useCallback(
    async (policyValue, incidentsToAdd) => {
      setLoadingState();
      const res = await previewPolicyChangeAddIncidents(policyValue, incidentsToAdd);
      if (res.ok) {
        const { preview } = res;
        setPreview(preview);
      } else {
        handleError(
          { ...res, tryAgain: handlePreviewPolicyChangeAddIncidents.bind(this, policyValue, incidentsToAdd) },
          { policyValue }
        );
      }
      setLoadingPreview(false);
    },
    [previewPolicyChangeAddIncidents]
  );

  const handleConfirmChanges = async ({
    previewId,
    internalNote,
    mailingAddress: incomingMailingAddress,
    policyPreviewUsername,
    ...farmUWAnswers
  }) => {
    const hasChangesThatNeedApproval =
      changesForApprovalConfig?.hasChangesThatNeedApproval || preview.needsUnderwritingReview;

    // Issue #31439: when a farm endorsement required UW review, forward the answers entered in
    // the FarmUWQuestionsModal so they're stored on the version-history entry and printed on the
    // documents. (`policyPreviewUsername` is destructured out so it isn't treated as an answer.)
    const isFarmUWReview = isFarmBasedPolicyType(policy.policyType) && preview?.needsUnderwritingReview;
    let uwReviewAnswers = isFarmUWReview && Object.keys(farmUWAnswers).length > 0 ? farmUWAnswers : undefined;

    // #32405: when a UW adds/changes/removes the restriction via the Details section (a change that
    // doesn't itself trip UW review), forward it so policy-mod rolls the FMI 122 form and
    // prints it. The FarmUWQuestionsModal path (isFarmUWReview) already carries restrictions.
    if (!uwReviewAnswers) {
      uwReviewAnswers = buildRestrictionUwReviewAnswers({
        policyType: policy.policyType,
        canApproveUWReview,
        isFarmUWReview,
        currentRestriction: getFarmPolicyRestriction(policy),
        // Apply the captured restriction only when it belongs to the preview being confirmed.
        editedRestriction: resolveEditedRestriction(editedRestrictionRef.current, previewId)
      });
    }

    const mailingAddressChanged = !isEqual(incomingMailingAddress, mailingAddress);

    if (action === 'cancel' && mailingAddressChanged) {
      const { data } = await updateAccount({
        id: accountId,
        fname: fname,
        lname: lname,
        email: email,
        phoneNumber: phoneNumber,
        mailingAddress: incomingMailingAddress
      });
      if (!data) {
        toast.notify({
          type: 'error',
          message:
            'Failed to update mailing address. Please try again. If the problem persists, please contact support.',
          label: 'Edit in Account Details',
          action: () => {
            history.push(`/customer/${accountId}?scrollTo=account-details`, { openAccountCard: true });
          }
        });
        return; // Don't proceed with cancellation if address update failed
      }
    }

    setLoadingState();
    let res = await confirm(policy.id, previewId, internalNote, uwReviewAnswers);
    // #32405: the edited restriction has been consumed for this confirm — clear it so a subsequent
    // reopened-preview confirm (e.g. a different previewId link opened without remounting) can't
    // forward a stale value. The next real Details edit re-captures it in handleSegmentChange.
    editedRestrictionRef.current = undefined;
    let hasError = !res.ok;
    if (res.ok) {
      if (hasChangesThatNeedApproval) {
        track('Change request - Approved', {
          isAgency,
          username: user.username
        });
      }

      res = await getRenewalPolicyId(policy.id);

      if (previewIdFromQueryParams) {
        removeQueryParamsFromUrl(history);
      }

      hasError = !res.ok;
      if (res.ok) {
        const { renewalPolicyId } = res;
        setActivePolicyModal(renewalPolicyId ? PolicyModalTypes.RENEWAL : null);
        setRenewalPolicyId(renewalPolicyId);
        clearPolicyPreview();

        toast.notify({
          type: 'success',
          message: 'The policy was updated successfully',
          label: "Go to Member's Details",
          action: () => {
            history.push(`/customer/${accountId}`);
          }
        });

        fetchFullAccountAndPolicy(policy.id, accountId);
      }
    }
    if (hasError) {
      handleError(
        {
          error: {
            message: friendlyErrorMessage(res.error.message),
            title: 'Confirmation error',
            label: 'Refresh page'
          }
        },
        { id: previewId, internalNote }
      );
    }
    setLoadingPreview(false);
  };

  const handleSubmitChangesForApproval = async ({ internalNote, previewId }) => {
    const policyPreviewLink = `${window.location.href.split('?')[0]}?previewId=${previewId}`;
    setLoadingState();
    const changesSummary = formatRequestedChangesText(
      parseRequestedChanges(preview?.diff, policy.segments[policy.segments.length - 1])
    );
    const res = await submitChangesForApproval(policy.id, policyPreviewLink, internalNote || '', changesSummary);

    if (res.ok) {
      setActivePolicyModal(PolicyModalTypes.SUBMITTED_FOR_APPROVAL);

      track('Change request - Submitted', {
        isAgency,
        username: user.username
      });
    } else {
      handleError(
        {
          error: {
            message: friendlyErrorMessage(res.error.message),
            title: 'Confirmation error',
            label: 'Refresh page'
          }
        },
        { id: policy.id, internalNote }
      );
    }

    setLoadingPreview(false);
  };

  const handleSubmitFarmPolicyForUnderwritingApproval = async (values) => {
    setLoadingState();
    const { internalNote, ...uwAnswers } = values;

    const res = await submitFarmPolicyForUnderwritingApproval(
      policy.id,
      preview?.id,
      policy.version,
      uwAnswers,
      internalNote
    );

    if (res.ok) {
      setActivePolicyModal(PolicyModalTypes.SUBMITTED_FOR_APPROVAL);
      clearPolicyPreview();

      track('Farm Policy UW Review - Submitted', {
        isAgency,
        username: user.username
      });
    } else {
      handleError(
        {
          error: {
            message: friendlyErrorMessage(res.error?.message),
            title: 'Submission error',
            label: 'Refresh page'
          }
        },
        { id: policy.id, values }
      );
    }

    setLoadingPreview(false);
  };

  const remountComponent = () => {
    setRemountKey((prev) => prev + 1);
  };

  return (
    <DisableProvider key={remountKey}>
      {!isLoading && notifications && <PolicyNotifications onClick={() => setCurrentTab(PolicyTabs.DETAILS)} />}
      {!isLoading && <PolicyRestrictionsBanner policy={policy} />}
      {!isLoading && <PolicyCommentsBanner policy={policy} />}
      {session.canViewPolicies && (
        <Tabs value={currentTab} variant="fullWidth" onChange={handleChangeTab}>
          <Tab label="Settings" className={classes.tab} value={PolicyTabs.SETTINGS} />
          <Tab label="Payment & Price" className={classes.tab} value={PolicyTabs.PAYMENT} />
          <Tab label="Details" className={classes.tab} value={PolicyTabs.DETAILS} />
          <Tab label="Documents" className={classes.tab} value={PolicyTabs.DOCUMENTS} />
          <Tab label="Incidents" className={classes.tab} value={PolicyTabs.INCIDENTS} />
        </Tabs>
      )}
      {/* Check styles for Section */}
      <Section className={classNames([classes.details, { [classes.withFooter]: changed }])}>
        {isLoading ? (
          <Loading type="secondary" />
        ) : (
          <>
            {currentTab === PolicyTabs.SETTINGS && session.canViewPolicies && (
              <PolicySettings
                key={`version-${policy.version}`}
                policy={policy}
                accountId={accountId}
                loadingPreview={loadingPreview}
                onPreviewPolicy={handleChangePolicy}
                goToPaymentTab={() => setCurrentTab(PolicyTabs.PAYMENT)}
                cancelledOrInCancellation={cancelledOrInCancellation}
                allowReinstatement={allowReinstatement}
                allowRescind={allowRescind}
                isCancelledGSNICPolicy={isCancelledGSNICPolicy}
                disableLeavePageModal={disableLeavePageModal}
              />
            )}
            {currentTab === PolicyTabs.PAYMENT && (
              <PaymentTab loadingPreview={loadingPreview} handleChangePolicy={handleChangePolicy} />
            )}
            {currentTab === PolicyTabs.DETAILS && (
              <PolicyDetails
                key={`version-${policy.version}`}
                loadingPreview={loadingPreview}
                onSegmentPreview={handleSegmentChange}
                repEmail={session.user.email}
                onClose={closeModal}
                preview={preview}
              />
            )}
            {(currentTab === PolicyTabs.DOCUMENTS || (!session.canEditPolicies && !session.viewOnly)) && (
              <Documents showRecreateApplication={session.canEditPolicies} />
            )}
            {currentTab === PolicyTabs.INCIDENTS && (
              <Incidents
                loadingPreview={loadingPreview}
                handleChangePolicy={handlePreviewPolicyChangeScrubIncidents}
                onAddIncident={handlePreviewPolicyChangeAddIncidents}
              />
            )}
          </>
        )}
      </Section>

      {activePolicyModal === PolicyModalTypes.PREVIEW && (
        <Preview
          open={activePolicyModal === PolicyModalTypes.PREVIEW}
          preview={preview}
          onClose={closeModal}
          onConfirm={
            changesForApprovalConfig?.hasChangesThatNeedApproval && !canApproveUWReview
              ? handleSubmitChangesForApproval
              : handleConfirmChanges
          }
          onContinueToUWReview={() => setActivePolicyModal(PolicyModalTypes.FARM_UW_QUESTIONS)}
          loading={loadingPreview}
          action={action}
          policyType={policy.policyType}
          cancelReason={reasons.cancelReason}
          nonRenewReason={reasons.nonRenewReason}
          isAgency={session.isAgency}
          changesForApprovalConfig={changesForApprovalConfig}
        />
      )}
      {activePolicyModal === PolicyModalTypes.RENEWAL && (
        <RenewalNotice
          open={activePolicyModal === PolicyModalTypes.RENEWAL}
          onClose={closeModal}
          renewalPolicyId={renewalPolicyId}
        />
      )}
      {activePolicyModal === PolicyModalTypes.WARNING && (
        <WarningModal
          open={activePolicyModal === PolicyModalTypes.WARNING}
          onClose={() => {
            setActivePolicyModal(PolicyModalTypes.PREVIEW);
          }}
          reason={preview.warning}
        />
      )}
      {showChangeDateConflict && (
        <WarningModal
          open={showChangeDateConflict}
          onClose={() => setShowChangeDateConflict(false)}
          reason="CHANGE_DATE_CONFLICT"
        />
      )}
      {activePolicyModal === PolicyModalTypes.SUBMITTED_FOR_APPROVAL && (
        <SubmittedForApprovalModal
          open={activePolicyModal === PolicyModalTypes.SUBMITTED_FOR_APPROVAL}
          onClose={() => {
            // The draft is now with underwriting. Purge it from the agent's view so they
            // can't keep seeing/editing it in changes history (ticket #31050). Clear
            // unconditionally for every policy type: the farm UW path already nulls the
            // store preview before this modal shows (so a previewId-gated cleanup would be
            // skipped), and closeModal's own cleanup also skips farm policies.
            // removeQueryParamsFromUrl is idempotent and router-aware.
            removeQueryParamsFromUrl(history);
            clearPolicyPreview();
            setPreview(null);
            closePolicyModal();
            // Reload the real, currently-endorsed policy (no previewId) so the draft segment
            // is gone for farm and home alike.
            fetchFullAccountAndPolicy(policy.id, accountId);
            // remount the component to reset all forms
            remountComponent();
          }}
        />
      )}
      {activePolicyModal === PolicyModalTypes.PREVIEW_LINK_EXPIRED && (
        <PolicyPreviewExpiredModal
          open={activePolicyModal === PolicyModalTypes.PREVIEW_LINK_EXPIRED}
          onClose={() => {
            clearPolicyPreview();
            closeModal();

            // remount the component to reset all forms
            remountComponent();
          }}
        />
      )}
      {activePolicyModal === PolicyModalTypes.FARM_UW_QUESTIONS && (
        <Suspense fallback={null}>
          <FarmUWQuestionsModal
            open={activePolicyModal === PolicyModalTypes.FARM_UW_QUESTIONS}
            onClose={() => setActivePolicyModal(PolicyModalTypes.PREVIEW)}
            onConfirm={canApproveUWReview ? handleConfirmChanges : handleSubmitFarmPolicyForUnderwritingApproval}
            preview={preview}
            wasRenewedFromImportedTerm={Boolean(wasRenewedFromImportedLineage(policy, policies))}
          />
        </Suspense>
      )}
      <LeavePageDialog
        message={
          previewIdFromQueryParams
            ? "Preview with requested changes will be closed. You'll be able to reopen it by following the preview link again. Are you sure you want to want to close it?"
            : undefined
        }
        title={previewIdFromQueryParams ? 'Close Preview' : undefined}
        onClose={() => setPromptTab(null)}
        open={!!promptTab}
        cb={() => {
          setCurrentTab(promptTab);
          setPromptTab(null);
          setChanged(false);

          if (previewIdFromQueryParams) {
            clearPolicyPreview();
          }
        }}
      />
      <ErrorDialog
        open={!!error.messages}
        errors={error.messages}
        onClose={() => setError({})}
        label={error.label}
        onClick={error.tryAgain || error.onClick}
        title={error.title}
        showGoHomeButton={!previewIdFromQueryParams}
      />
      {currentTab !== PolicyTabs.SETTINGS &&
        (isImportedAndNotEndorseable(policy) ? (
          <ImportedPolicyFooter />
        ) : (
          cancelled && <CancelledPolicyFooter policyStatus={policyStatus} isAgency={session.isAgency} policy={policy} />
        ))}
    </DisableProvider>
  );
};

Policy.propTypes = {
  history: PropTypes.object.isRequired
};

export default observer(Policy);
